import type { JSX } from 'react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useSearchParams } from 'react-router';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Badge, Button, useToast } from '@artosera/ui';
import { usePeut } from '../../../auth/usePeut.js';
import { formaterDate } from '../../../lib/format.js';
import { listerLots, lotEnTravail, type LotReprise } from '../../../lib/reprise.js';
import pageStyles from '../../../styles/page.module.css';
import tableStyles from '../../../styles/table.module.css';
import { NouveauLotDepot } from './correspondance/NouveauLotDepot.js';
import { NouveauLotFichiers } from './images/NouveauLotFichiers.js';
import { DepotDialog } from './DepotDialog.js';
import { etatAfficheLot, messageReprise } from './messages.js';
import { ProgressionTravail } from './plan/ProgressionTravail.js';
import styles from './Reprise.module.css';

/** La liste des lots tient dans 100 lignes : c'est la borne de l'API. */
const LIMITE_LOTS = 100;

export function cleLots(idGalerie: string): readonly string[] {
  return ['console', 'galerie', idGalerie, 'reprise', 'lots'];
}

/**
 * Les compteurs d'un lot, tels que son rapport les a posés. Un lot jamais
 * essayé n'en a pas : on le dit au lieu d'afficher des zéros.
 */
function Resultat({ lot }: { lot: LotReprise }): JSX.Element {
  const { t } = useTranslation('console');
  const compteurs = lot.compteurs;
  // Un lot tiré d'un dépôt n'a pas de compteurs : ses morceaux en ont.
  if (lot.morceaux && lot.morceaux.morceaux > 0)
    return (
      <span>
        {t('correspondance.liste.morceaux', {
          count: lot.morceaux.morceaux,
          prets: lot.morceaux.etats['pret'] ?? 0,
        })}
        <span className={`${styles.discret} ${styles.bloc}`}>
          {t('reprise.lots.lignes', {
            count: lot.morceaux.liste.reduce((s, m) => s + m.lignes, 0),
          })}
        </span>
      </span>
    );
  // Un lot d'images se rapproche d'abord : la liste dit où il en est, avec les compteurs de l'API.
  if (lot.rapprochement?.fait && lot.rapprochement.compteurs && compteurs === null)
    return (
      <span>
        {lot.rapprochement.compteurs.ambigus > 0
          ? t('images.liste.ambigus', { count: lot.rapprochement.compteurs.ambigus })
          : t('images.liste.rapproche', { count: lot.rapprochement.compteurs.rapproches })}
        <span className={`${styles.discret} ${styles.bloc}`}>
          {t('images.liste.images', { count: lot.rapprochement.compteurs.images })}
        </span>
      </span>
    );
  if (compteurs === null)
    return (
      <span className={styles.discret}>
        {lot.parcours === 'images'
          ? t('images.liste.aRapprocher')
          : lot.origine === 'depot' && lot.parcours !== 'fichiers'
            ? t('correspondance.liste.depuisDepot')
            : t('reprise.lots.nonEssaye')}
      </span>
    );
  return (
    <span>
      {t('reprise.lots.compteurs', {
        crees: compteurs.crees,
        completes: compteurs.completes,
        ignores: compteurs.ignores,
        refuses: compteurs.refuses,
      })}
      <span className={`${styles.discret} ${styles.bloc}`}>
        {t('reprise.lots.lignes', { count: compteurs.total })}
      </span>
    </span>
  );
}

/** Les lots de reprise de la galerie : nature, état, résultat, opérateur, date. */
export function LotsBloc({ idGalerie }: { idGalerie: string }): JSX.Element {
  const { t } = useTranslation('console');
  const peut = usePeut();
  const client = useQueryClient();
  const { toast } = useToast();
  const [depotOuvert, setDepotOuvert] = useState(false);
  const [lotDepotOuvert, setLotDepotOuvert] = useState(false);
  const [fichiersOuvert, setFichiersOuvert] = useState(false);
  const [, setParametres] = useSearchParams();

  const lots = useQuery({
    queryKey: cleLots(idGalerie),
    queryFn: () => listerLots(idGalerie),
    // Un lot qui travaille se suit : la liste se relit comme sa fiche.
    refetchInterval: (requete) => (requete.state.data?.some(lotEnTravail) ? 3000 : false),
  });

  return (
    <section className={pageStyles.panneau} aria-labelledby="reprise-lots">
      <div className={pageStyles.sectionTitre}>
        <h2 id="reprise-lots">{t('reprise.lots.titre')}</h2>
        {peut('console:run-import') ? (
          <div className={styles.actions}>
            <Button variant="secondary" onClick={() => setFichiersOuvert(true)}>
              {t('images.liste.bouton')}
            </Button>
            <Button variant="secondary" onClick={() => setLotDepotOuvert(true)}>
              {t('correspondance.liste.depuisDepotBouton')}
            </Button>
            <Button onClick={() => setDepotOuvert(true)}>{t('reprise.lots.nouveau')}</Button>
          </div>
        ) : null}
      </div>
      <div className={styles.pile}>
        <p className={styles.texte}>{t('reprise.lots.texte')}</p>

        {lots.isLoading ? <p className={pageStyles.videEtat}>{t('reprise.chargement')}</p> : null}
        {lots.isError ? (
          <p className={styles.erreur} role="alert">
            {messageReprise(t, lots.error) ?? t('reprise.lots.erreur')}
          </p>
        ) : null}
        {lots.isSuccess && lots.data.length === 0 ? (
          <p className={pageStyles.videEtat}>{t('reprise.lots.aucun')}</p>
        ) : null}

        {lots.isSuccess && lots.data.length > 0 ? (
          <table className={`${tableStyles.table} ${styles.table}`}>
            <thead>
              <tr>
                <th scope="col">{t('reprise.lots.colonnes.nature')}</th>
                <th scope="col">{t('reprise.lots.colonnes.etat')}</th>
                <th scope="col">{t('reprise.lots.colonnes.compteurs')}</th>
                <th scope="col">{t('reprise.lots.colonnes.operateur')}</th>
                <th scope="col" className={styles.colonneEtroite}>
                  {t('reprise.lots.colonnes.depose')}
                </th>
              </tr>
            </thead>
            <tbody>
              {lots.data.map((lot) => (
                <tr key={lot.id}>
                  <td data-label={t('reprise.lots.colonnes.nature')}>
                    <Link
                      className={styles.lienLot}
                      to={`?lot=${encodeURIComponent(lot.id)}`}
                      aria-label={t('reprise.lots.ouvrir', {
                        nature: t(`reprise.natures.${lot.nature}`),
                      })}
                    >
                      {t(`reprise.natures.${lot.nature}`)}
                    </Link>
                  </td>
                  <td data-label={t('reprise.lots.colonnes.etat')}>
                    <Badge tone={etatAfficheLot(lot).ton}>{t(etatAfficheLot(lot).cle)}</Badge>
                  </td>
                  <td data-label={t('reprise.lots.colonnes.compteurs')}>
                    <div>
                      <Resultat lot={lot} />
                      <ProgressionTravail
                        travail={lot.annulation?.travail ?? lot.travail}
                        actif={lotEnTravail(lot)}
                        compact
                      />
                    </div>
                  </td>
                  <td data-label={t('reprise.lots.colonnes.operateur')}>{lot.operateur}</td>
                  <td data-label={t('reprise.lots.colonnes.depose')}>
                    {formaterDate(lot.creeLe, '—')}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : null}
        {lots.isSuccess && lots.data.length >= LIMITE_LOTS ? (
          <p className={styles.discret}>{t('reprise.lots.limite')}</p>
        ) : null}
      </div>

      <NouveauLotDepot
        idGalerie={idGalerie}
        ouvert={lotDepotOuvert}
        onFermer={() => setLotDepotOuvert(false)}
        onCree={(lot) => {
          setLotDepotOuvert(false);
          toast({ title: t('correspondance.nouveau.ok'), closeLabel: t('fiche.domaines.fermer') });
          void client.invalidateQueries({ queryKey: cleLots(idGalerie) });
          setParametres({ lot: lot.id });
        }}
      />

      <NouveauLotFichiers
        idGalerie={idGalerie}
        ouvert={fichiersOuvert}
        onFermer={() => setFichiersOuvert(false)}
        onCree={(lot) => {
          setFichiersOuvert(false);
          toast({
            title: t(
              lot.nature === 'images' ? 'images.nouveau.okImages' : 'images.nouveau.okFichiers',
            ),
            closeLabel: t('fiche.domaines.fermer'),
          });
          void client.invalidateQueries({ queryKey: cleLots(idGalerie) });
          setParametres({ lot: lot.id });
        }}
      />

      <DepotDialog
        idGalerie={idGalerie}
        ouvert={depotOuvert}
        onFermer={() => setDepotOuvert(false)}
        onDepose={(lot) => {
          setDepotOuvert(false);
          toast({ title: t('reprise.depot.ok'), closeLabel: t('fiche.domaines.fermer') });
          void client.invalidateQueries({ queryKey: cleLots(idGalerie) });
          // Le lot déposé s'ouvre : l'étape suivante est son essai.
          setParametres({ lot: lot.id });
        }}
      />
    </section>
  );
}
